import React from "react";
import ServiceLayout from "../../components/services/ServiceLayout";
import { 
  Shield, 
  Link as LinkIcon, 
  Layers, 
  Cpu, 
  Monitor, 
  CheckCircle, 
  Activity, 
  Smile 
} from "lucide-react";
import image1 from '../../Assets/Services/Service8-2.jpg'
import image2 from '../../Assets/Services/Service8-3.jpg'


const CrownsBridgesDentures = () => {
  return (
    <ServiceLayout
      title="Crowns, Bridges & Dentures"
      subtitle="“Restore your smile, confidence, and ability to eat comfortably again."
      heroImage="/images/hero/prosthetics.jpg"
      images={[image1, image2]}
    >
      {/* Introduction */}
      <section className="mb-12">
        <p className="text-lg text-gray-700 leading-relaxed">
          Missing or damaged teeth can affect not only your ability to chew and speak comfortably but also
your confidence in your smile. Modern prosthetic dentistry offers advanced solutions to restore
both function and appearance, helping you enjoy everyday life again.
        </p>
        <p className="text-lg text-gray-700 leading-relaxed">
          At Dr. Sridhar International Dental Hospitals, we provide advanced crowns, bridges, and denture
treatments using modern digital technology to restore proper chewing ability, clear speech, and a
natural, confident smile.
        </p>
      </section>

      {/* Main Restorative Pillars */}
      <section className="grid md:grid-cols-3 gap-6 mb-16">
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-blue-50 hover:border-blue-200 transition">
          <Shield className="text-blue-600 w-10 h-10 mb-4" />
          <h3 className="text-xl font-bold text-blue-900 mb-2">Dental Crowns</h3>
          <p className="text-sm text-gray-600">Custom-made caps designed to protect and strengthen damaged or weakened teeth while
restoring their natural shape, function, and beautiful appearance.</p>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-blue-50 hover:border-blue-200 transition">
          <LinkIcon className="text-blue-600 w-10 h-10 mb-4" />
          <h3 className="text-xl font-bold text-blue-900 mb-2">Dental Bridges</h3>
          <p className="text-sm text-gray-600">A reliable solution to replace missing teeth by securely anchoring artificial teeth to neighboring
teeth or implants, restoring your smile and chewing ability.</p>
        </div>
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-blue-50 hover:border-blue-200 transition">
          <Layers className="text-blue-600 w-10 h-10 mb-4" />
          <h3 className="text-xl font-bold text-blue-900 mb-2">Dentures</h3>
          <p className="text-sm text-gray-600">Comfortable and natural-looking removable teeth designed to replace multiple missing teeth or
full tooth loss, helping you smile, speak, and eat with confidence again.</p>
        </div>
      </section>

      {/* Crowns Deep Dive */}
      <section className="mb-16">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 border-l-4 border-blue-600 pl-4">Why do you need a Dental Crown?</h2>
        <p className="text-gray-700 mb-6">Crowns are commonly recommended for:</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {[
            "Teeth with large cavities",
            "Teeth after root canal treatment",
            "Broken or fractured teeth",
            "Severely worn teeth",
            "Cosmetic improvement of discolored teeth"
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-3 bg-gray-50 p-3 rounded-lg border border-gray-100">
              <CheckCircle className="text-blue-500 w-5 h-5 flex-shrink-0" />
              <span className="text-gray-700 font-medium">{item}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Technology Feature Block */}
      <section className="bg-blue-900 text-white p-10 rounded-3xl mb-16 shadow-xl relative overflow-hidden">
        <div className="relative z-10">
          <h2 className="text-3xl font-bold mb-6 flex items-center gap-3">
            <Cpu className="text-blue-300" /> Single Visit CAD/CAM Dentistry
          </h2>
          <p className="text-blue-100 text-lg mb-8 max-w-2xl leading-relaxed">
            Using <strong>Dentsply Sirona CAD/CAM technology</strong>, we design and fabricate high-precision ceramic and zirconia crowns in a single visit. 
          </p>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-white/10 p-6 rounded-xl border border-white/20">
              <Monitor className="text-blue-300 mb-3" />
              <h4 className="font-bold mb-2">Digital Impressions</h4>
              <p className="text-sm text-blue-100">Say goodbye to uncomfortable manual impressions. We capture highly accurate 3D images of your teeth instantly.</p>
            </div>
            <div className="bg-white/10 p-6 rounded-xl border border-white/20">
              <CheckCircle className="text-blue-300 mb-3" />
              <h4 className="font-bold mb-2">Perfect Fit & Comfort</h4>
              <p className="text-sm text-blue-100">Computerized manufacturing ensures every crown or bridge fits perfectly with your natural bite.</p>
            </div>
          </div>
        </div>
        <Cpu className="absolute -bottom-10 -right-10 w-72 h-72 text-white/5" />
      </section>

      {/* Materials & Solutions Grid */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-center text-blue-900 mb-12">Restorative Solutions We Offer</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { icon: <Activity />, label: "Ceramic Crowns", sub: "Natural Aesthetics" },
            { icon: <Shield />, label: "Zirconia Crowns", sub: "Maximum Strength" },
            { icon: <Layers />, label: "Implant Supported", sub: "Secure Foundations" },
            { icon: <Smile />, label: "Digital Dentures", sub: "Precise Jaw Fit" },
          ].map((sol, i) => (
            <div key={i} className="text-center p-6 bg-white border border-gray-100 rounded-2xl hover:shadow-md transition">
              <div className="text-blue-600 flex justify-center mb-3">{sol.icon}</div>
              <h4 className="font-bold text-gray-900 text-sm">{sol.label}</h4>
              <p className="text-xs text-gray-500">{sol.sub}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits Summary Footer */}
      <section className="border-t pt-12 bg-gray-50 p-8 rounded-2xl">
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">The Result? A Natural Smile.</h2>
            <p className="text-gray-600 leading-relaxed">
              Dental bridges help prevent the shifting of nearby teeth and support your natural facial structure,
while crowns restore the strength and function of damaged teeth.
            </p>
            <p className="text-gray-600 leading-relaxed">
              At Dr. Sridhar International Dental Hospitals, our specialists carefully evaluate each patient to
recommend the most suitable materials and treatment options, ensuring long-lasting durability,
comfort, and a natural-looking smile.
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            {["PFM Crowns", "Ceramic", "Zirconia", "Implant Crowns", "Fixed Bridges", "Complete Dentures"].map(tag => (
              <span key={tag} className="px-3 py-1 bg-white border border-gray-200 text-blue-700 text-xs font-bold rounded-full">
                {tag}
              </span>
            ))}
          </div>
        </div>
      </section>
    </ServiceLayout>
  );
};

export default CrownsBridgesDentures;